import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, Mail, Phone, Globe, ArrowRight, Download } from "lucide-react";
import { toast } from "sonner";

/* ============================================================
   TEMPLATE 1 — "Bold Card" (matches select-advance-style reference)
   Table-based, email-client safe.
   ============================================================ */
/* ============================================================
   TEMPLATE 1 — "Bold Card"  (white bg, brand-only colors, tight)
   ============================================================ */
function buildBoldCardHTML({ name, title, phone, email, site, applyUrl, logoUrl }) {
  const NAVY = "#0A1326";
  const GOLD = "#B8962C";      // darker gold reads well on white
  const GOLD_BRIGHT = "#D4AF37";
  const MUTED = "#6B7280";
  const BORDER = "#E5E7EB";
  const safe = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;color:${NAVY};line-height:1.5;">
  <tr>
    <td style="padding:0;vertical-align:top;">
      <table cellpadding="0" cellspacing="0" border="0"><tr>
        <td style="padding:0 28px 0 0;vertical-align:top;border-right:1px solid ${BORDER};">
          <a href="https://directfundcap.com" style="text-decoration:none;color:inherit;">
            <img src="${logoUrl}" alt="DirectFundCap" width="200" height="46" style="display:block;border:0;outline:none;" />
          </a>
          <div style="margin-top:16px;">
            <div style="font-size:10px;letter-spacing:2.4px;color:${MUTED};font-weight:700;">BUSINESS FUNDING</div>
            <div style="font-size:17px;font-weight:800;color:${NAVY};letter-spacing:-0.3px;margin-top:4px;">
              DONE&nbsp;<span style="border-bottom:2px solid ${GOLD_BRIGHT};padding-bottom:1px;">RIGHT.</span>
            </div>
          </div>
        </td>
        <td style="padding:0 0 0 28px;vertical-align:top;">
          <div style="font-size:18px;font-weight:700;color:${NAVY};letter-spacing:-0.2px;line-height:1.25;">${safe(name)}</div>
          <div style="font-size:10px;letter-spacing:2.4px;color:${GOLD};font-weight:700;margin-top:4px;text-transform:uppercase;">${safe(title)}</div>
          <table cellpadding="0" cellspacing="0" border="0" style="margin-top:14px;">
            ${phone ? `<tr><td style="padding:1px 10px 1px 0;color:${GOLD};font-size:12px;line-height:1;">☎</td><td style="padding:1px 0;font-size:13px;line-height:1.4;"><a href="tel:${safe(phone).replace(/[^0-9+]/g,'')}" style="text-decoration:none;color:${NAVY};">${safe(phone)}</a></td></tr>` : ""}
            ${email ? `<tr><td style="padding:1px 10px 1px 0;color:${GOLD};font-size:12px;line-height:1;">✉</td><td style="padding:1px 0;font-size:13px;line-height:1.4;"><a href="mailto:${safe(email)}" style="text-decoration:none;color:${NAVY};">${safe(email)}</a></td></tr>` : ""}
            ${site ? `<tr><td style="padding:1px 10px 1px 0;color:${GOLD};font-size:12px;line-height:1;">⌘</td><td style="padding:1px 0;font-size:13px;line-height:1.4;"><a href="https://${safe(site)}" style="text-decoration:none;color:${NAVY};">${safe(site)}</a></td></tr>` : ""}
          </table>
          <div style="margin-top:16px;">
            <a href="${safe(applyUrl)}" style="display:inline-block;background:${NAVY};color:#FFFFFF;text-decoration:none;padding:9px 18px;font-size:11px;font-weight:700;letter-spacing:2px;text-transform:uppercase;">APPLY NOW &nbsp;→</a>
          </div>
        </td>
      </tr></table>

      <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-top:18px;border-top:1px solid ${BORDER};">
        <tr>
          <td style="padding-top:10px;font-size:10px;letter-spacing:2px;color:${NAVY};font-weight:700;">
            <span style="color:${GOLD};">✓</span>&nbsp;&nbsp;<span style="border-bottom:1px dotted ${GOLD_BRIGHT};padding-bottom:1px;">TRUSTED.</span>&nbsp;
            <span style="border-bottom:1px dotted ${GOLD_BRIGHT};padding-bottom:1px;">RELIABLE.</span>&nbsp;
            <span style="border-bottom:1px dotted ${GOLD_BRIGHT};padding-bottom:1px;">RESPONSIVE.</span>
          </td>
          <td align="right" style="padding-top:10px;font-size:10px;letter-spacing:2px;color:${NAVY};font-weight:700;">
            <a href="https://${safe(site)}" style="text-decoration:none;color:${NAVY};border-bottom:1px solid ${NAVY};padding-bottom:1px;">${safe(site)}</a>
          </td>
        </tr>
      </table>

      <div style="margin-top:12px;font-size:9.5px;line-height:1.55;color:#9CA3AF;max-width:560px;">
        This e-mail and any attachments may contain confidential information intended solely for the named addressee(s). If you are not the intended recipient, any review, use, disclosure, or distribution of this e-mail is strictly prohibited — please notify the sender and delete this message.
      </div>
    </td>
  </tr>
</table>`;
}

/* ============================================================
   TEMPLATE 2 — "Minimal" (matches FunduguCapital-style reference)
   Simple two-column: logo + tagline | name/title/address/contact.
   ============================================================ */
/* ============================================================
   TEMPLATE 2 — "Minimal"  (white bg, tight logo + contact block)
   ============================================================ */
function buildMinimalHTML({ name, title, phone, email, site, address1, address2, logoUrl }) {
  const NAVY = "#0A1326";
  const GOLD = "#B8962C";
  const MUTED = "#6B7280";
  const BORDER = "#E5E7EB";
  const safe = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;color:${NAVY};line-height:1.5;">
  <tr>
    <td style="padding:0 32px 0 0;vertical-align:middle;border-right:1px solid ${BORDER};">
      <a href="https://directfundcap.com" style="text-decoration:none;color:inherit;">
        <img src="${logoUrl}" alt="DirectFundCap" width="200" height="46" style="display:block;border:0;outline:none;" />
      </a>
      <div style="margin-top:10px;font-size:10px;letter-spacing:3px;color:${GOLD};font-weight:700;text-align:center;">
        BUSINESS · CAPITAL
      </div>
    </td>
    <td style="padding:0 0 0 32px;vertical-align:middle;">
      <div style="font-size:15px;font-weight:700;color:${NAVY};letter-spacing:-0.1px;">
        ${safe(name)}${title ? `<span style="color:${GOLD};font-weight:400;"> &nbsp;|&nbsp; </span>${safe(title)}` : ""}
      </div>
      <table cellpadding="0" cellspacing="0" border="0" style="margin-top:10px;font-size:13px;color:${NAVY};line-height:1.55;">
        ${address1 ? `<tr><td style="padding:0;color:${MUTED};">${safe(address1)}</td></tr>` : ""}
        ${address2 ? `<tr><td style="padding:0;color:${MUTED};">${safe(address2)}</td></tr>` : ""}
        ${phone ? `<tr><td style="padding:2px 0 0 0;"><span style="color:${MUTED};">O.</span>&nbsp;<a href="tel:${safe(phone).replace(/[^0-9+]/g,'')}" style="color:${NAVY};text-decoration:none;border-bottom:1px solid ${GOLD};padding-bottom:1px;">${safe(phone)}</a></td></tr>` : ""}
        ${email ? `<tr><td style="padding:2px 0 0 0;"><a href="mailto:${safe(email)}" style="color:${NAVY};text-decoration:none;border-bottom:1px solid ${GOLD};padding-bottom:1px;">${safe(email)}</a></td></tr>` : ""}
        ${site ? `<tr><td style="padding:2px 0 0 0;"><a href="https://${safe(site)}" style="color:${NAVY};text-decoration:none;border-bottom:1px solid ${GOLD};padding-bottom:1px;">${safe(site)}</a></td></tr>` : ""}
      </table>
    </td>
  </tr>
</table>`;
}

/* ============================================================
   TEMPLATE 3 — "Executive"  (single-line ultra-compact)
   ============================================================ */
function buildExecutiveHTML({ name, title, phone, email, site, logoUrl }) {
  const NAVY = "#0A1326";
  const GOLD = "#B8962C";
  const MUTED = "#6B7280";
  const safe = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;color:${NAVY};line-height:1.5;">
  <tr>
    <td style="padding:0;vertical-align:middle;">
      <a href="https://directfundcap.com" style="text-decoration:none;color:inherit;">
        <img src="${logoUrl}" alt="DirectFundCap" width="170" height="39" style="display:block;border:0;outline:none;" />
      </a>
      <table cellpadding="0" cellspacing="0" border="0" style="margin-top:10px;">
        <tr>
          <td style="padding:0;font-size:14px;color:${NAVY};letter-spacing:-0.1px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;font-size:12px;">
            ${safe(name)}
          </td>
          <td style="padding:0 10px;color:${GOLD};font-size:12px;">/</td>
          <td style="padding:0;font-size:12px;color:${MUTED};letter-spacing:1.5px;text-transform:uppercase;">
            ${safe(title)}
          </td>
        </tr>
      </table>
      <table cellpadding="0" cellspacing="0" border="0" style="margin-top:6px;">
        <tr>
          ${phone ? `<td style="padding:0;font-size:13px;color:${NAVY};"><a href="tel:${safe(phone).replace(/[^0-9+]/g,'')}" style="color:${NAVY};text-decoration:none;">${safe(phone)}</a></td><td style="padding:0 10px;color:${GOLD};font-size:12px;">·</td>` : ""}
          ${email ? `<td style="padding:0;font-size:13px;color:${NAVY};"><a href="mailto:${safe(email)}" style="color:${NAVY};text-decoration:none;">${safe(email)}</a></td><td style="padding:0 10px;color:${GOLD};font-size:12px;">·</td>` : ""}
          ${site ? `<td style="padding:0;font-size:13px;color:${NAVY};"><a href="https://${safe(site)}" style="color:${NAVY};text-decoration:none;border-bottom:1px solid ${GOLD};padding-bottom:1px;">${safe(site)}</a></td>` : ""}
        </tr>
      </table>
    </td>
  </tr>
</table>`;
}

/* ============================================================
   TEMPLATE 4 — "Wall Street"  (editorial with credentials)
   ============================================================ */
function buildWallStreetHTML({ name, title, phone, email, site, logoUrl }) {
  const NAVY = "#0A1326";
  const GOLD = "#B8962C";
  const GOLD_BRIGHT = "#D4AF37";
  const MUTED = "#6B7280";
  const BORDER = "#E5E7EB";
  const safe = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;color:${NAVY};line-height:1.5;max-width:560px;">
  <tr>
    <td style="padding:0;">
      <a href="https://directfundcap.com" style="text-decoration:none;color:inherit;">
        <img src="${logoUrl}" alt="DirectFundCap" width="210" height="48" style="display:block;border:0;outline:none;" />
      </a>
      <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-top:14px;border-top:2px solid ${NAVY};">
        <tr><td style="padding:12px 0 4px 0;font-size:16px;font-weight:700;color:${NAVY};letter-spacing:-0.2px;">
          ${safe(name)}<span style="color:${GOLD};font-weight:400;">&nbsp;&nbsp;|&nbsp;&nbsp;</span><span style="text-transform:uppercase;letter-spacing:2px;font-size:11px;color:${GOLD};font-weight:700;">${safe(title)}</span>
        </td></tr>
      </table>

      <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-top:10px;">
        <tr>
          <td style="padding:6px 0 6px 0;font-size:10px;letter-spacing:2px;color:${MUTED};font-weight:700;text-transform:uppercase;border-top:1px solid ${BORDER};border-bottom:1px solid ${BORDER};">
            Credentials
          </td>
        </tr>
        <tr>
          <td style="padding:8px 0 0 0;font-family:'Courier New',Courier,monospace;font-size:12px;color:${NAVY};letter-spacing:0.5px;">
            <span style="color:${GOLD_BRIGHT};font-weight:700;">$500M+</span>&nbsp;FUNDED
            &nbsp;·&nbsp;
            <span style="color:${GOLD_BRIGHT};font-weight:700;">10,000+</span>&nbsp;BUSINESSES
            &nbsp;·&nbsp;
            <span style="color:${GOLD_BRIGHT};font-weight:700;">24-HR</span>&nbsp;APPROVALS
          </td>
        </tr>
      </table>

      <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-top:14px;border-top:1px solid ${BORDER};">
        <tr>
          ${phone ? `<td style="padding:10px 12px 0 0;font-size:13px;color:${NAVY};"><span style="color:${GOLD};font-weight:700;">T.</span>&nbsp;<a href="tel:${safe(phone).replace(/[^0-9+]/g,'')}" style="color:${NAVY};text-decoration:none;">${safe(phone)}</a></td>` : ""}
          ${email ? `<td style="padding:10px 12px 0 0;font-size:13px;color:${NAVY};"><span style="color:${GOLD};font-weight:700;">E.</span>&nbsp;<a href="mailto:${safe(email)}" style="color:${NAVY};text-decoration:none;">${safe(email)}</a></td>` : ""}
          ${site ? `<td style="padding:10px 0 0 0;font-size:13px;color:${NAVY};"><span style="color:${GOLD};font-weight:700;">W.</span>&nbsp;<a href="https://${safe(site)}" style="color:${NAVY};text-decoration:none;border-bottom:1px solid ${GOLD};padding-bottom:1px;">${safe(site)}</a></td>` : ""}
        </tr>
      </table>
    </td>
  </tr>
</table>`;
}

/* ============================================================
   TEMPLATE 5 — "Broker Card"  (business-card w/ gold accent bar)
   ============================================================ */
function buildBrokerCardHTML({ name, title, phone, email, site, applyUrl, logoUrl }) {
  const NAVY = "#0A1326";
  const GOLD = "#B8962C";
  const GOLD_BRIGHT = "#D4AF37";
  const MUTED = "#6B7280";
  const safe = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;color:${NAVY};line-height:1.5;">
  <tr>
    <td style="padding:0;vertical-align:middle;background:${GOLD_BRIGHT};width:4px;"></td>
    <td style="padding:6px 0 6px 22px;vertical-align:middle;">
      <a href="https://directfundcap.com" style="text-decoration:none;color:inherit;">
        <img src="${logoUrl}" alt="DirectFundCap" width="200" height="46" style="display:block;border:0;outline:none;" />
      </a>
      <div style="margin-top:14px;font-size:16px;font-weight:700;color:${NAVY};letter-spacing:-0.2px;">${safe(name)}</div>
      <div style="margin-top:2px;font-size:11px;letter-spacing:2px;color:${GOLD};font-weight:700;text-transform:uppercase;">${safe(title)}</div>
      <table cellpadding="0" cellspacing="0" border="0" style="margin-top:12px;">
        <tr>
          ${phone ? `<td style="padding:0 16px 0 0;font-size:12px;color:${NAVY};"><span style="color:${GOLD};">☎</span>&nbsp;<a href="tel:${safe(phone).replace(/[^0-9+]/g,'')}" style="color:${NAVY};text-decoration:none;">${safe(phone)}</a></td>` : ""}
          ${email ? `<td style="padding:0 16px 0 0;font-size:12px;color:${NAVY};"><span style="color:${GOLD};">✉</span>&nbsp;<a href="mailto:${safe(email)}" style="color:${NAVY};text-decoration:none;">${safe(email)}</a></td>` : ""}
          ${site ? `<td style="padding:0;font-size:12px;color:${NAVY};"><span style="color:${GOLD};">⌘</span>&nbsp;<a href="https://${safe(site)}" style="color:${NAVY};text-decoration:none;">${safe(site)}</a></td>` : ""}
        </tr>
      </table>
      ${applyUrl ? `<div style="margin-top:14px;"><a href="${safe(applyUrl)}" style="display:inline-block;color:${NAVY};text-decoration:none;font-size:11px;font-weight:700;letter-spacing:2px;text-transform:uppercase;border-bottom:2px solid ${GOLD_BRIGHT};padding-bottom:2px;">Apply for funding &nbsp;→</a></div>` : ""}
      <div style="margin-top:14px;font-size:9.5px;letter-spacing:2px;color:${MUTED};text-transform:uppercase;font-weight:700;">
        Trusted&nbsp;·&nbsp;Reliable&nbsp;·&nbsp;Responsive
      </div>
    </td>
  </tr>
</table>`;
}

/* ============================================================
   TEMPLATE 6 — "Stacked Elite"  (narrow vertical, editorial serif tone)
   ============================================================ */
function buildStackedEliteHTML({ name, title, phone, email, site, logoUrl }) {
  const NAVY = "#0A1326";
  const GOLD = "#B8962C";
  const GOLD_BRIGHT = "#D4AF37";
  const MUTED = "#6B7280";
  const safe = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:'Georgia','Times New Roman',serif;color:${NAVY};line-height:1.55;max-width:340px;">
  <tr>
    <td style="padding:0;">
      <a href="https://directfundcap.com" style="text-decoration:none;color:inherit;">
        <img src="${logoUrl}" alt="DirectFundCap" width="180" height="41" style="display:block;border:0;outline:none;" />
      </a>
      <div style="width:36px;height:2px;background:${GOLD_BRIGHT};margin:14px 0 12px 0;"></div>
      <div style="font-size:19px;font-weight:400;color:${NAVY};letter-spacing:-0.2px;font-family:'Georgia',serif;">
        ${safe(name)}
      </div>
      <div style="margin-top:2px;font-family:Arial,Helvetica,sans-serif;font-size:10px;letter-spacing:2.5px;color:${GOLD};font-weight:700;text-transform:uppercase;">
        ${safe(title)}
      </div>
      <table cellpadding="0" cellspacing="0" border="0" style="margin-top:14px;font-family:Arial,Helvetica,sans-serif;font-size:13px;color:${NAVY};">
        ${phone ? `<tr><td style="padding:1px 0;color:${MUTED};width:22px;font-weight:700;">O.</td><td style="padding:1px 0;"><a href="tel:${safe(phone).replace(/[^0-9+]/g,'')}" style="color:${NAVY};text-decoration:none;">${safe(phone)}</a></td></tr>` : ""}
        ${email ? `<tr><td style="padding:1px 0;color:${MUTED};font-weight:700;">E.</td><td style="padding:1px 0;"><a href="mailto:${safe(email)}" style="color:${NAVY};text-decoration:none;border-bottom:1px solid ${GOLD};padding-bottom:1px;">${safe(email)}</a></td></tr>` : ""}
        ${site ? `<tr><td style="padding:1px 0;color:${MUTED};font-weight:700;">W.</td><td style="padding:1px 0;"><a href="https://${safe(site)}" style="color:${NAVY};text-decoration:none;border-bottom:1px solid ${GOLD};padding-bottom:1px;">${safe(site)}</a></td></tr>` : ""}
      </table>
      <div style="margin-top:14px;padding-top:12px;border-top:1px solid #E5E7EB;font-family:'Georgia',serif;font-size:12px;color:${MUTED};font-style:italic;">
        “Direct capital. No runaround.”
      </div>
    </td>
  </tr>
</table>`;
}

const TEMPLATES = [
  { id: "bold",      label: "Bold Card",     description: "Two-column with logo lockup, CTA button, trust bar." },
  { id: "minimal",   label: "Minimal",       description: "Compact logo + contact block. Clean and lightweight." },
  { id: "executive", label: "Executive",     description: "Ultra-compact single-line. Ideal for high-volume senders." },
  { id: "wallst",    label: "Wall Street",   description: "Editorial with credentials line ($500M+ funded)." },
  { id: "broker",    label: "Broker Card",   description: "Business-card feel with gold accent bar." },
  { id: "stacked",   label: "Stacked Elite", description: "Vertical serif signature with a closing quote." },
];

const BUILDERS = {
  bold: buildBoldCardHTML,
  minimal: buildMinimalHTML,
  executive: buildExecutiveHTML,
  wallst: buildWallStreetHTML,
  broker: buildBrokerCardHTML,
  stacked: buildStackedEliteHTML,
};

export default function SignatureBuilder() {
  const [template, setTemplate] = useState("bold");
  const [form, setForm] = useState({
    name: "Joey Barnes",
    title: "Head of Funding Operations",
    phone: "",
    email: "joey@directfundcap.com",
    site: "directfundcap.com",
    applyUrl: "https://directfundcap.com#apply",
    address1: "199 Water St",
    address2: "New York, NY 10038",
  });
  const [copied, setCopied] = useState(false);

  const logoUrl = `${window.location.origin}/logo-full-light.png`;

  const html = useMemo(() => {
    const builder = BUILDERS[template] || BUILDERS.bold;
    return builder({ ...form, logoUrl });
  }, [template, form, logoUrl]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const copyRich = async () => {
    try {
      if (window.ClipboardItem && navigator.clipboard?.write) {
        const blob = new Blob([html], { type: "text/html" });
        const textBlob = new Blob([html], { type: "text/plain" });
        await navigator.clipboard.write([new window.ClipboardItem({ "text/html": blob, "text/plain": textBlob })]);
      } else {
        await navigator.clipboard.writeText(html);
      }
      setCopied(true);
      toast.success("Signature copied. Paste it into your email signature settings.");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Copy failed — use the Download HTML button instead.");
    }
  };

  const downloadHtml = () => {
    const blob = new Blob(
      [`<!doctype html><html><head><meta charset="utf-8"><title>DirectFundCap Signature</title></head><body>${html}</body></html>`],
      { type: "text/html" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `directfundcap-signature-${template}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="signature-builder-page" className="min-h-screen bg-[#050B14] text-white">
      <header className="border-b border-white/10 sticky top-0 bg-[#050B14]/90 backdrop-blur z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 border border-gold flex items-center justify-center">
              <div className="w-1.5 h-1.5 bg-gold rotate-45" />
            </div>
            <span className="font-heading text-base">Direct<span className="text-gold">Fund</span>Cap</span>
          </Link>
          <span className="text-xs tracking-[0.2em] uppercase text-white/40">Email Signature Builder</span>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-gold mb-4">Your Details</div>
          <h1 className="font-heading text-3xl md:text-4xl font-light tracking-tight mb-8">
            Build your DirectFundCap signature.
          </h1>

          {/* Template picker */}
          <div className="mb-8">
            <div className="text-xs tracking-[0.18em] uppercase text-white/60 mb-3">Template</div>
            <div className="grid grid-cols-2 gap-2">
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTemplate(t.id)}
                  data-testid={`template-${t.id}`}
                  className={`text-left p-3 border transition-all ${
                    template === t.id
                      ? "border-gold bg-gold/5 text-white"
                      : "border-white/10 hover:border-white/30 text-white/70"
                  }`}
                >
                  <div className="text-sm font-semibold">{t.label}</div>
                  <div className="text-[11px] text-white/50 mt-0.5 leading-snug">{t.description}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-5">
            <Field label="Full Name"><input data-testid="sig-name" value={form.name} onChange={update("name")} className="sig-input" /></Field>
            <Field label="Title"><input data-testid="sig-title" value={form.title} onChange={update("title")} className="sig-input" /></Field>
            {template === "minimal" && (
              <>
                <Field label="Address Line 1"><input data-testid="sig-addr1" value={form.address1} onChange={update("address1")} className="sig-input" /></Field>
                <Field label="Address Line 2"><input data-testid="sig-addr2" value={form.address2} onChange={update("address2")} className="sig-input" /></Field>
              </>
            )}
            <Field label="Phone"><input data-testid="sig-phone" value={form.phone} onChange={update("phone")} className="sig-input" /></Field>
            <Field label="Email"><input data-testid="sig-email" value={form.email} onChange={update("email")} className="sig-input" /></Field>
            <Field label="Website"><input data-testid="sig-site" value={form.site} onChange={update("site")} className="sig-input" /></Field>
            {template === "bold" && (
              <Field label='"Apply Now" link'><input data-testid="sig-apply" value={form.applyUrl} onChange={update("applyUrl")} className="sig-input" /></Field>
            )}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={copyRich} data-testid="sig-copy" className="inline-flex items-center gap-2 bg-gold hover:bg-gold-400 text-navy-950 font-semibold px-6 py-3 transition-all hover:-translate-y-0.5">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? "Copied" : "Copy Signature"}
            </button>
            <button onClick={downloadHtml} data-testid="sig-download" className="inline-flex items-center gap-2 border border-white/20 hover:border-gold text-white px-6 py-3 transition-all">
              <Download className="w-4 h-4" /> Download HTML
            </button>
          </div>

          <div className="mt-10 border border-white/10 p-5 text-sm text-white/60 leading-relaxed">
            <div className="text-xs tracking-[0.2em] uppercase text-gold font-bold mb-3">How to install</div>
            <ol className="space-y-2 list-decimal pl-4">
              <li>Click <b className="text-white">Copy Signature</b>.</li>
              <li><b className="text-white">Gmail</b>: Settings → See all settings → Signature → paste.</li>
              <li><b className="text-white">Outlook Web</b>: Settings → Compose and reply → paste.</li>
              <li><b className="text-white">Apple Mail</b>: Preferences → Signatures → paste (turn OFF &quot;Always match my default font&quot;).</li>
              <li>Save. Send a test email to yourself to confirm.</li>
            </ol>
          </div>
        </div>

        <div className="lg:col-span-8">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs tracking-[0.25em] uppercase font-bold text-gold">Live Preview</div>
            <div className="text-xs text-white/40">Template: <span className="text-white">{TEMPLATES.find((t) => t.id === template)?.label}</span></div>
          </div>

          <div className="bg-white text-neutral-900 shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
            <div className="border-b border-neutral-200 px-5 py-3 flex items-center justify-between">
              <div className="text-sm font-semibold">New Message</div>
              <div className="flex gap-1.5">
                <span className="w-3 h-3 rounded-full bg-neutral-300" />
                <span className="w-3 h-3 rounded-full bg-neutral-300" />
                <span className="w-3 h-3 rounded-full bg-neutral-300" />
              </div>
            </div>
            <div className="px-5 py-3 text-sm text-neutral-500 border-b border-neutral-100">To</div>
            <div className="px-5 py-3 text-sm text-neutral-500 border-b border-neutral-100">Subject</div>
            <div className="px-5 py-8 min-h-[60px]">—</div>
            <div className="px-5 pb-8">
              <div data-testid="signature-preview" dangerouslySetInnerHTML={{ __html: html }} />
            </div>
          </div>

          <details className="mt-6 border border-white/10 p-5">
            <summary className="cursor-pointer text-sm text-white/70 hover:text-gold">View raw HTML</summary>
            <pre className="mt-4 text-[11px] leading-relaxed text-white/60 overflow-x-auto whitespace-pre-wrap break-words font-mono">{html}</pre>
          </details>
        </div>
      </div>

      <style>{`
        .sig-input { width: 100%; background: #050B14; border: 1px solid rgba(255,255,255,0.12); color: #F8F9FA; padding: 12px 14px; font-size: 0.95rem; transition: border-color .2s ease, box-shadow .2s ease; }
        .sig-input:focus { outline: none; border-color: #D4AF37; box-shadow: 0 0 0 3px rgba(212,175,55,0.15); }
        .sig-input::placeholder { color: rgba(255,255,255,0.3); }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs tracking-[0.18em] uppercase text-white/60 mb-2 block">{label}</span>
      {children}
    </label>
  );
}

export const _icons = { Mail, Phone, Globe, ArrowRight };
